// Public Self-Apply Page — prospective external partner applies to join (BE-54 V1.5).
//
// URL: /apply (PUBLIC — no auth required)
//
// A prospective seller / production partner / vendor submits an application
// WITHOUT waiting for an admin invite. The submission lands in the admin review
// queue (SelfApplyReviewPage); on approval the admin issues a real onboarding
// invitation through the existing flow (D-5 admin-mediation). This page only
// captures the lead.
//
// Backend: POST /api/v1/self-apply (public). MSW intercepts in maket mode.

import { useState } from 'react'
import {
  Mail,
  Building2,
  User as UserIcon,
  Globe,
  MessageSquare,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Ticket,
} from 'lucide-react'
import { useMutation } from '@tanstack/react-query'
import { API_BASE } from '../../lib/api'
import { Card } from '../../components/ui/card'
import { useT } from '../../i18n'
import { selfApplyI18n } from '../../i18n/onboarding/self-apply'

type SelfApplyT = (typeof selfApplyI18n)['en']

// Role codes are API values; labels come from the dictionary (t.roles[code]).
const ROLE_OPTIONS = ['BO2', 'SO2', 'SO3'] as const

type SelfApplyBody = {
  applicant_email: string
  target_org_type: string
  org_name: string
  contact_name?: string
  message?: string
  website?: string
  referral_code?: string
}

// D30 referral capture: an educator/affiliate shares /apply?ref=CODE. Unknown
// codes are dropped server-side without an error, so this stays optional UX.
function referralCodeFromUrl(): string {
  try {
    return new URLSearchParams(window.location.search).get('ref')?.trim() ?? ''
  } catch {
    return ''
  }
}

type SelfApplyRead = {
  id: string
  applicant_email: string
  target_org_type: string
  org_name: string | null
  status: string
}

async function submitSelfApply(body: SelfApplyBody, t: SelfApplyT): Promise<SelfApplyRead> {
  const res = await fetch(`${API_BASE}/self-apply`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as {
      detail?: { code?: string; message?: string }
    }
    if (res.status === 409) {
      throw new Error(
        err.detail?.message ?? t.errors.alreadyUnderReview,
      )
    }
    if (res.status === 422) {
      throw new Error(t.errors.invalidForm)
    }
    throw new Error(err.detail?.message ?? t.errors.submissionFailed(res.status))
  }
  return res.json()
}

function PublicShell({ children }: { children: React.ReactNode }) {
  const t = useT(selfApplyI18n)
  return (
    <div className="flex-1 bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white flex items-center justify-center p-6">
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 text-indigo-600">
            <Mail size={20} aria-hidden="true" />
            <span className="font-semibold text-lg tracking-tight">PEXIFY</span>
          </div>
          <p className="text-[11px] text-slate-400 mt-1">
            {t.tagline}
          </p>
        </div>
        {children}
      </div>
    </div>
  )
}

export function SelfApplyPage() {
  const t = useT(selfApplyI18n)
  const [orgName, setOrgName] = useState('')
  const [email, setEmail] = useState('')
  const [orgType, setOrgType] = useState('')
  const [contactName, setContactName] = useState('')
  const [website, setWebsite] = useState('')
  const [message, setMessage] = useState('')
  const [referralCode, setReferralCode] = useState(referralCodeFromUrl)
  const [submitted, setSubmitted] = useState(false)

  const mutation = useMutation({
    mutationFn: () =>
      submitSelfApply({
        applicant_email: email.trim(),
        target_org_type: orgType,
        org_name: orgName.trim(),
        contact_name: contactName.trim() || undefined,
        website: website.trim() || undefined,
        message: message.trim() || undefined,
        referral_code: referralCode.trim() || undefined,
      }, t),
  })

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
  const formValid = orgName.trim().length > 0 && emailValid && orgType !== ''

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitted(true)
    if (!formValid) return
    mutation.mutate()
  }

  if (mutation.isSuccess) {
    return (
      <PublicShell>
        <Card className="p-6 text-center space-y-3 bg-emerald-50/40 border-emerald-200">
          <CheckCircle2 size={36} className="text-emerald-600 mx-auto" aria-hidden="true" />
          <h1 className="text-lg font-semibold text-slate-900">{t.success.title}</h1>
          <p className="text-sm text-slate-600">
            {t.success.bodyBefore}
            <span className="font-medium text-slate-800">{email.trim()}</span>
            {t.success.bodyAfter}
          </p>
          <div className="pt-2">
            <a
              href="https://pexify.net/"
              className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-1"
            >
              {t.success.backLink}
              <ArrowRight size={11} aria-hidden="true" />
            </a>
          </div>
        </Card>
      </PublicShell>
    )
  }

  return (
    <PublicShell>
      <Card className="p-6 space-y-5">
        <div className="space-y-1">
          <h1 className="text-lg font-semibold text-slate-900">{t.form.title}</h1>
          <p className="text-sm text-slate-600">
            {t.form.intro}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3" noValidate>
          <Field label={t.form.orgNameLabel} icon={Building2} required>
            <input
              type="text"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              placeholder="Thoughtful Gifts Co"
              className={inputClass}
            />
          </Field>
          {submitted && orgName.trim().length === 0 && (
            <FieldError>{t.form.orgNameRequired}</FieldError>
          )}

          <Field label={t.form.emailLabel} icon={Mail} required>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourbusiness.com"
              autoComplete="email"
              className={inputClass}
            />
          </Field>
          {submitted && !emailValid && (
            <FieldError>{t.form.emailInvalid}</FieldError>
          )}

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              {t.form.roleLabel} <span className="text-rose-500">*</span>
            </label>
            <select
              value={orgType}
              onChange={(e) => setOrgType(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">{t.form.rolePlaceholder}</option>
              {ROLE_OPTIONS.map((code) => (
                <option key={code} value={code}>
                  {t.roles[code]}
                </option>
              ))}
            </select>
          </div>
          {submitted && orgType === '' && (
            <FieldError>{t.form.roleRequired}</FieldError>
          )}

          <Field label={t.form.contactNameLabel} icon={UserIcon}>
            <input
              type="text"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              placeholder="Sarah Johnson"
              className={inputClass}
            />
          </Field>

          <Field label={t.form.websiteLabel} icon={Globe}>
            <input
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://yourbusiness.com"
              className={inputClass}
            />
          </Field>

          <Field label={t.form.referralLabel} icon={Ticket}>
            <input
              type="text"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
              maxLength={32}
              placeholder={t.form.referralPlaceholder}
              className={inputClass}
            />
          </Field>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              <span className="inline-flex items-center gap-1.5">
                <MessageSquare size={13} className="text-slate-400" aria-hidden="true" />
                {t.form.messageLabel}
              </span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder={t.form.messagePlaceholder}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y"
            />
          </div>

          {mutation.error && (
            <div className="p-2 bg-rose-50 border border-rose-200 rounded text-xs text-rose-700 flex items-start gap-1.5">
              <AlertCircle size={11} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
              <span>{String(mutation.error.message ?? mutation.error)}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={mutation.isPending}
            className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
          >
            {mutation.isPending ? (
              t.form.submitting
            ) : (
              <>
                {t.form.submit}
                <ArrowRight size={14} aria-hidden="true" />
              </>
            )}
          </button>

          <p className="text-[11px] text-slate-400 text-center pt-1">
            {t.form.alreadyInvited}{' '}
            <a href="/app/login" className="text-indigo-600 hover:underline">
              {t.form.signIn}
            </a>
          </p>
        </form>
      </Card>
    </PublicShell>
  )
}

const inputClass =
  'w-full pl-8 pr-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500'

function Field({
  label,
  icon: Icon,
  required,
  children,
}: {
  label: string
  icon: typeof Mail
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-700 mb-1">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      <div className="relative">
        <Icon
          size={14}
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
          aria-hidden="true"
        />
        {children}
      </div>
    </div>
  )
}

function FieldError({ children }: { children: React.ReactNode }) {
  return <p className="text-[11px] text-rose-600 -mt-2">{children}</p>
}
